import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authAPI } from '../services/api';
import ApiService from '../services/api';

// Token management utilities
const TOKEN_KEY = 'auth_token';
const TOKEN_EXPIRY_KEY = 'auth_token_expiry';
const USER_KEY = 'auth_user';
const EXPERIENCES_KEY = 'user_experiences';
const PROJECTS_KEY = 'user_projects';
const EDUCATION_KEY = 'user_education';

// Helper functions for token management
const setTokenData = (token, user) => {
  console.log('setTokenData called with:', { token: !!token, user: user });
  
  if (token) {
    try {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      // Set token expiry to 24 hours from now (adjust as needed)
      const expiryTime = new Date().getTime() + (24 * 60 * 60 * 1000);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
      
      // Verify the data was stored correctly
      const storedUser = JSON.parse(localStorage.getItem(USER_KEY) || '{}');
      console.log('Data stored in localStorage:', { 
        token: !!localStorage.getItem(TOKEN_KEY),
        user: storedUser,
        expiry: localStorage.getItem(TOKEN_EXPIRY_KEY)
      });
    } catch (error) {
      console.error('Error updating localStorage:', error);
    }
  } else {
    // No token provided, clearing localStorage
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  }
};

// Direct localStorage update function for user data
const updateUserInLocalStorage = (userData) => {
  try {
    const currentUser = JSON.parse(localStorage.getItem(USER_KEY) || '{}');
    const updatedUser = { ...currentUser, ...userData };
    localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
    
    // Direct localStorage update completed
    
    return updatedUser;
  } catch (error) {
    console.error('Error updating user in localStorage:', error);
    return null;
  }
};

// Cache management functions
const cacheUserData = async (token) => {
  try {
    // Fetch experiences and projects in parallel
    const [experiencesResponse, projectsResponse] = await Promise.all([
      ApiService.get('/experiences'),
      ApiService.get('/personal-projects')
    ]);

    // Cache experiences
    if (experiencesResponse.success) {
      localStorage.setItem(EXPERIENCES_KEY, JSON.stringify(experiencesResponse.data || []));
    }

    // Cache projects
    if (projectsResponse.success) {
      localStorage.setItem(PROJECTS_KEY, JSON.stringify(projectsResponse.data || []));
    }

    console.log('User data cached successfully');
  } catch (error) {
    console.error('Error caching user data:', error);
  }
};

const getCachedExperiences = () => {
  try {
    const cached = localStorage.getItem(EXPERIENCES_KEY);
    return cached ? JSON.parse(cached) : [];
  } catch (error) {
    console.error('Error retrieving cached experiences:', error);
    return [];
  }
};

const getCachedProjects = () => {
  try {
    const cached = localStorage.getItem(PROJECTS_KEY);
    return cached ? JSON.parse(cached) : [];
  } catch (error) {
    console.error('Error retrieving cached projects:', error);
    return [];
  }
};

const getCachedEducation = () => {
  try {
    const cached = localStorage.getItem(EDUCATION_KEY);
    return cached ? JSON.parse(cached) : [];
  } catch (error) {
    console.error('Error retrieving cached education:', error);
    return [];
  }
};

const clearUserCache = () => {
  localStorage.removeItem(EXPERIENCES_KEY);
  localStorage.removeItem(PROJECTS_KEY);
  localStorage.removeItem(EDUCATION_KEY);
};

const updateCachedProject = (projectId, updatedProject) => {
  try {
    const cached = getCachedProjects();
    const updatedProjects = cached.map(project => 
      project.id === projectId ? { ...project, ...updatedProject } : project
    );
    localStorage.setItem(PROJECTS_KEY, JSON.stringify(updatedProjects));
    return updatedProjects;
  } catch (error) {
    console.error('Error updating cached project:', error);
    return getCachedProjects(); // Return the cached projects instead of undefined 'cached'
  }
};

const getTokenData = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  const userStr = localStorage.getItem(USER_KEY);
  const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
  
  let user = null;
  try {
    user = userStr ? JSON.parse(userStr) : null;
  } catch (e) {
    console.error('Error parsing user data from localStorage:', e);
  }
  
  const expiry = expiryStr ? parseInt(expiryStr) : null;
  
  return { token, user, expiry };
};

const isTokenExpired = () => {
  const { token, expiry } = getTokenData();
  
  // If no token exists, consider it expired
  if (!token) {
    // No token found in localStorage, considered expired
    return true;
  }
  
  // If no expiry exists, consider it expired
  if (!expiry) {
    // No expiry found in localStorage, token considered expired
    return true;
  }
  
  const currentTime = new Date().getTime();
  const isExpired = currentTime > expiry;
  
  if (isExpired) {
    // Token expired
  } else {
    // Token is valid
  }
  
  return isExpired;
};

const clearTokenData = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
};

// Utility function to check if user should be redirected to login
const shouldRedirectToLogin = () => {
  const { token, expiry } = getTokenData();
  
  // If no token, redirect to login
  if (!token) {
    return true;
  }
  
  // If no expiry, redirect to login
  if (!expiry) {
    return true;
  }
  
  // If token is expired, redirect to login
  const currentTime = new Date().getTime();
  if (currentTime > expiry) {
    return true;
  }
  
  return false;
};

// Safe token validation that doesn't clear data
const isTokenValid = () => {
  const { token, expiry } = getTokenData();
  
  // isTokenValid check
  
  // If no token exists, invalid
  if (!token) {
    // No token found, invalid
    return false;
  }
  
  // If no expiry exists, invalid
  if (!expiry) {
    // No expiry found, invalid
    return false;
  }
  
  // Check if token is expired
  const currentTime = new Date().getTime();
  const isValid = currentTime <= expiry;
  // Token validation result
  return isValid;
};

// Initial state
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  tokenExpired: false,
  justAuthenticated: false, // Flag to prevent immediate token expiration checks
};

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  REGISTER_START: 'REGISTER_START',
  REGISTER_SUCCESS: 'REGISTER_SUCCESS',
  REGISTER_FAILURE: 'REGISTER_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_USER: 'SET_USER',
  UPDATE_USER: 'UPDATE_USER',
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  AUTO_LOGOUT: 'AUTO_LOGOUT',
  CLEAR_JUST_AUTHENTICATED: 'CLEAR_JUST_AUTHENTICATED',
};

// Reducer function
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
    case AUTH_ACTIONS.REGISTER_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
    case AUTH_ACTIONS.REGISTER_SUCCESS:
      // Store token and user data in localStorage for both login and registration
      // Also store suspension_notice if present
      const userData = action.payload.user;
      if (action.payload.suspension_notice) {
        userData.suspension_notice = action.payload.suspension_notice;
      }
      setTokenData(action.payload.token, userData);
      // Auth success - token stored, user authenticated
      return {
        ...state,
        user: userData,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        tokenExpired: false,
        justAuthenticated: false, // Disabled flag since we're not using interval checks
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
    case AUTH_ACTIONS.REGISTER_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };

    case AUTH_ACTIONS.LOGOUT:
    case AUTH_ACTIONS.AUTO_LOGOUT:
      // Clear token data from localStorage
      clearTokenData();
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        tokenExpired: false,
      };

    case AUTH_ACTIONS.TOKEN_EXPIRED:
      return {
        ...state,
        tokenExpired: false, // Disabled to prevent annoying messages
        isAuthenticated: false,
        error: null, // Disabled error message
      };

    case AUTH_ACTIONS.SET_USER:
      // Ensure suspension_notice is preserved if it exists in the payload
      const userPayload = action.payload;
      if (userPayload && !userPayload.suspension_notice && state.user?.suspension_notice) {
        // Preserve existing suspension_notice if not in new payload
        userPayload.suspension_notice = state.user.suspension_notice;
      }
      return {
        ...state,
        user: userPayload,
        isAuthenticated: !!userPayload,
        isLoading: false,
        justAuthenticated: false, // Keep flag disabled since we're not using interval checks
      };

    case AUTH_ACTIONS.UPDATE_USER:
      // Update user data and also update localStorage
      const updatedUser = { ...state.user, ...action.payload };
      // UPDATE_USER: About to update localStorage
      
      // Method 1: Use setTokenData if we have a token
      if (state.token) {
        setTokenData(state.token, updatedUser);
        // Method 1: setTokenData completed
      } else {
        // No token available for setTokenData
      }
      
      // Method 2: Direct localStorage update as backup
      const directUpdateResult = updateUserInLocalStorage(action.payload);
      // Method 2: Direct localStorage update result
      
      // Final verification
      const finalStoredUser = JSON.parse(localStorage.getItem(USER_KEY) || '{}');
      // Final verification - localStorage user data
      
      return {
        ...state,
        user: updatedUser,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case AUTH_ACTIONS.CLEAR_JUST_AUTHENTICATED:
      return {
        ...state,
        justAuthenticated: false,
      };

    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Handle browser close/unload to notify backend
  useEffect(() => {
    if (!state.isAuthenticated || !state.user) {
      return;
    }

    const handleBeforeUnload = async () => {
      // Notify backend that user is going offline
      try {
        const { pusherService } = await import('../services/pusherService');
        await pusherService.notifyUserOffline();
      } catch (error) {
        console.error('Error notifying offline status on unload:', error);
      }
    };

    // Use beforeunload for browser close
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Also handle visibility change (tab switch, minimize, etc.)
    const handleVisibilityChange = async () => {
      if (document.hidden) {
        // Tab is hidden, but don't mark as offline immediately
        // Wait a bit to see if it comes back
        setTimeout(async () => {
          if (document.hidden) {
            // Still hidden after delay, notify offline
            try {
              const { pusherService } = await import('../services/pusherService');
              await pusherService.notifyUserOffline();
            } catch (error) {
              console.error('Error notifying offline status on visibility change:', error);
            }
          }
        }, 30000); // 30 seconds delay
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [state.isAuthenticated, state.user]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      const { token, user, expiry } = getTokenData();
      
      // Initial auth check
      
      // First check if token exists in localStorage
      if (!token) {
        // No token found in localStorage, user not authenticated
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        return;
      }
      
      // Check if token is valid using localStorage
      if (!isTokenValid()) {
        // Token is invalid or expired, clearing data and user not authenticated
        clearTokenData();
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        return;
      }
      
      // Token exists and is not expired, set user as authenticated
      if (user) {
        // Valid token found, setting user as authenticated from localStorage
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user });
      }
      
      // Backend verification to get fresh user data
      try {
        console.log('Verifying token with backend...');
        const response = await authAPI.getCurrentUser();
        if (response.success) {
          console.log('Backend verification successful, updating user data');
          // Update user data with fresh data from backend
          // Include suspension_notice if present
          const userData = response.data.user;
          if (response.data.suspension_notice) {
            userData.suspension_notice = response.data.suspension_notice;
          }
          dispatch({
            type: AUTH_ACTIONS.SET_USER,
            payload: userData,
          });
          // Update stored user data
          setTokenData(token, userData);
          
          // Initialize Pusher connection after successful backend verification
          if (response.data.user && response.data.user.id) {
            import('../services/pusherService').then(({ pusherService }) => {
              pusherService.initialize(response.data.user.id, response.data.user.role);
            });
          }
        } else {
          console.log('Backend verification failed, but keeping user authenticated with localStorage data');
        }
      } catch (error) {
        console.error('Backend verification failed:', error);
        // Don't logout on network errors, keep user authenticated with localStorage data
        if (error.status === 401) {
          console.log('401 error - token invalid on backend, clearing localStorage');
          clearTokenData();
          dispatch({ type: AUTH_ACTIONS.TOKEN_EXPIRED });
        } else {
          console.log('Network error during backend verification, keeping user authenticated');
        }
      }
    };

    checkAuth();
  }, []);

  // DISABLED: Token expiration check interval to prevent loops
  // useEffect(() => {
  //   // Only set up interval if user is authenticated and not just authenticated
  //   if (!state.isAuthenticated || state.justAuthenticated) {
  //     return;
  //   }

  //   const checkTokenExpiration = () => {
  //     console.log('Checking token expiration...');
      
  //     // Check if token is still valid
  //     if (!isTokenValid()) {
  //       console.log('Token is no longer valid, logging out');
  //       clearTokenData();
  //       dispatch({ type: AUTH_ACTIONS.TOKEN_EXPIRED });
  //     } else {
  //       const { expiry } = getTokenData();
  //       console.log('Token is still valid, expires at:', new Date(expiry));
  //     }
  //   };

  //   // Add a delay before starting the interval to prevent immediate checks after registration
  //   let intervalId = null;
  //   const timeoutId = setTimeout(() => {
  //     console.log('Starting token expiration check interval');
  //     // Check every 5 minutes to reduce false positives
  //     intervalId = setInterval(checkTokenExpiration, 300000);
  //   }, 30000); // 30 second delay
    
  //   return () => {
  //     clearTimeout(timeoutId);
  //     if (intervalId) {
  //       clearInterval(intervalId);
  //     }
  //   };
  // }, [state.isAuthenticated, state.justAuthenticated]);

  // DISABLED: Clear the justAuthenticated flag after a delay (not needed without interval)
  // useEffect(() => {
  //   if (state.justAuthenticated) {
  //     const timeoutId = setTimeout(() => {
  //       console.log('Clearing justAuthenticated flag');
  //       dispatch({ type: AUTH_ACTIONS.CLEAR_JUST_AUTHENTICATED });
  //     }, 60000); // 60 second delay to give more time for stable authentication
      
  //     return () => clearTimeout(timeoutId);
  //   }
  // }, [state.justAuthenticated]);

  // DISABLED: Listen for token expiration events from API service to prevent loops
  // useEffect(() => {
  //   const handleTokenExpired = () => {
  //     dispatch({ type: AUTH_ACTIONS.TOKEN_EXPIRED });
  //   };

  //   window.addEventListener('auth:token-expired', handleTokenExpired);
    
  //   return () => {
  //     window.removeEventListener('auth:token-expired', handleTokenExpired);
  //   };
  // }, []);

  // Login function
  const login = async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    
    try {
      const response = await authAPI.login(credentials);
      
      if (response.success) {
        console.log('Login successful, storing user data:', response.data.user);
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            user: response.data.user,
            token: response.data.token,
            suspension_notice: response.data.suspension_notice || null,
          },
        });
        
        // Cache user data after successful login
        await cacheUserData(response.data.token);
        
        // Verify data was stored in localStorage
        const storedUser = JSON.parse(localStorage.getItem(USER_KEY) || '{}');
        console.log('User data stored in localStorage:', storedUser);
        
        // Initialize Pusher connection after successful login
        if (response.data.user && response.data.user.id) {
          import('../services/pusherService').then(({ pusherService }) => {
            pusherService.initialize(response.data.user.id, response.data.user.role);
          });
        }
        
        return { success: true, data: response.data };
      } else {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: response.message || 'Login failed',
        });
        return { success: false, error: response.message || 'Login failed' };
      }
    } catch (error) {
      // Handle validation errors from Laravel
      if (error.status === 422 && error.errors) {
        // Return validation errors in a structured format
        return { 
          success: false, 
          validationErrors: error.errors,
          error: error.message || 'Validation failed'
        };
      }
      
      const errorMessage = error.message || 'Login failed';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  // Sign in with Google via Firebase, then exchange the ID token for a Sanctum token
  const loginWithGoogle = async () => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const { signInWithGoogle } = await import('../firebase');
      const idToken = await signInWithGoogle();
      const response = await authAPI.googleLogin(idToken);

      if (response.success) {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            user: response.data.user,
            token: response.data.token,
            suspension_notice: response.data.suspension_notice || null,
          },
        });

        await cacheUserData(response.data.token);

        if (response.data.user && response.data.user.id) {
          import('../services/pusherService').then(({ pusherService }) => {
            pusherService.initialize(response.data.user.id, response.data.user.role);
          });
        }

        return { success: true, data: response.data };
      }

      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: response.message || 'Google sign-in failed',
      });
      return { success: false, error: response.message || 'Google sign-in failed' };
    } catch (error) {
      // Always surface the raw failure - a popup that dies on a Firebase config
      // problem otherwise looks identical to the user simply closing it.
      console.error('Google sign-in failed:', error.code, error.message, error);

      // A deliberate cancel is not worth an error banner, but LOGIN_START already
      // flipped isLoading, so clear that too rather than just the error.
      if (error.code === 'auth/cancelled-popup-request') {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
        return { success: false, cancelled: true };
      }

      const messagesByCode = {
        'auth/popup-blocked':
          'Your browser blocked the Google sign-in popup. Please allow popups and try again.',
        'auth/popup-closed-by-user':
          'The Google sign-in window closed before sign-in finished. Please try again.',
        'auth/configuration-not-found':
          'Google sign-in is not set up on the Firebase project yet (Authentication has not been enabled).',
        'auth/operation-not-allowed':
          'Google sign-in is not enabled for this project. Enable the Google provider in the Firebase console.',
        'auth/unauthorized-domain':
          'This domain is not authorised for Google sign-in. Add it under Firebase Authentication > Settings > Authorized domains.',
      };

      const errorMessage =
        messagesByCode[error.code] ||
        (error.code ? `Google sign-in failed (${error.code}).` : error.message) ||
        'Google sign-in failed';

      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  // Register function
  const register = async (userData) => {
    dispatch({ type: AUTH_ACTIONS.REGISTER_START });
    
    try {
      const response = await authAPI.register(userData);
      
      if (response.success) {
        console.log('Registration successful, storing user data:', response.data.user);
        dispatch({
          type: AUTH_ACTIONS.REGISTER_SUCCESS,
          payload: {
            user: response.data.user,
            token: response.data.token,
          },
        });
        
        // Cache user data after successful registration
        await cacheUserData(response.data.token);
        
        // Verify data was stored in localStorage
        const storedUser = JSON.parse(localStorage.getItem(USER_KEY) || '{}');
        console.log('User data stored in localStorage:', storedUser);
        
        return { success: true, data: response.data };
      } else {
        dispatch({
          type: AUTH_ACTIONS.REGISTER_FAILURE,
          payload: response.message || 'Registration failed',
        });
        return { success: false, error: response.message || 'Registration failed' };
      }
    } catch (error) {
      // Handle validation errors from Laravel
      if (error.status === 422 && error.errors) {
        // Return validation errors in a structured format
        return { 
          success: false, 
          validationErrors: error.errors,
          error: error.message || 'Validation failed'
        };
      }
      
      const errorMessage = error.message || 'Registration failed';
      dispatch({
        type: AUTH_ACTIONS.REGISTER_FAILURE,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Disconnect Pusher before clearing data
      import('../services/pusherService').then(({ pusherService }) => {
        pusherService.disconnect();
      });
      
      // Always clear local data and logout
      clearTokenData();
      clearUserCache();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  // Force logout (for token expiration)
  const forceLogout = () => {
    // Disconnect Pusher before clearing data
    import('../services/pusherService').then(({ pusherService }) => {
      pusherService.disconnect();
    });
    
    clearTokenData();
    dispatch({ type: AUTH_ACTIONS.AUTO_LOGOUT });
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Update user data function
  const updateUser = (userData) => {
    // updateUser called
    dispatch({ type: AUTH_ACTIONS.UPDATE_USER, payload: userData });
  };

  // Switch mode (personal/recruiter/company). For company mode, optional companyUserId selects which company.
  // When backend returns a new token (auth switch), we replace token and user so the app acts as that identity.
  const switchMode = async (mode, acceptTerms = false, setAsDefault = false, companyUserId = null) => {
    if (!state.user) return { success: false, message: 'User not authenticated' };
    if (!['personal', 'recruiter', 'company'].includes(mode)) {
      return { success: false, message: 'Invalid mode' };
    }
    try {
      const body = { mode, accept_terms: acceptTerms, set_as_default: setAsDefault };
      if (mode === 'company' && companyUserId) body.company_user_id = companyUserId;
      const response = await ApiService.post('/user/switch-mode', body);
      if (!response.success) {
        return { success: false, message: response.message };
      }
      const data = response.data || {};
      // Auth switch: backend returned a new token (acting as company or back to parent)
      if (data.token && data.user) {
        setTokenData(data.token, data.user);
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: data.user });
        return { success: true, data };
      }
      // Same identity, mode updated (e.g. parent switching personal <-> recruiter)
      if (data.user) {
        updateUser(data.user);
      } else {
        updateUser({
          active_mode: data.active_mode,
          default_mode: data.default_mode,
          ...(data.active_company != null && { active_company: data.active_company }),
          ...(data.company_profile != null && { company_profile: data.company_profile }),
        });
      }
      return { success: true, data };
    } catch (err) {
      const errorMsg = err?.response?.data?.message || err?.message || 'Failed to switch mode';
      return { success: false, message: errorMsg };
    }
  };

  // Apply auth switch (new token + user). Use after create-company or when switch-mode returns a new token.
  const applyAuthSwitch = (token, user) => {
    if (token && user) {
      setTokenData(token, user);
      dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user });
    }
  };

  // Manual localStorage update function (backup method)
  const forceUpdateLocalStorage = (userData) => {
    // forceUpdateLocalStorage called
    const result = updateUserInLocalStorage(userData);
    if (result) {
      // Also update the context state
      dispatch({ type: AUTH_ACTIONS.UPDATE_USER, payload: userData });
    }
    return result;
  };

  // Context value
  const value = {
    ...state,
    login,
    loginWithGoogle,
    register,
    logout,
    forceLogout,
    clearError,
    updateUser,
    switchMode,
    applyAuthSwitch,
    forceUpdateLocalStorage,
    getCachedExperiences,
    getCachedProjects,
    getCachedEducation,
    cacheUserData,
    updateCachedProject,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export default AuthContext;
